import { createSlice } from '@reduxjs/toolkit';
import { SettingsState } from './types';
import {
  changePassword,
  deactivateAccount,
  getInvestorProfile,
  updateInvestorProfile,
  getSubscription,
  getTransactions,
} from './settingsActions';

const initialState: SettingsState = {
  investorProfile: null,
  subscription: null,
  transactions: [],
  transactionsTotal: 0,
  loading: false,
  error: null,
  passwordChangeLoading: false,
  passwordChangeError: null,
  deactivateLoading: false,
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearPasswordChangeError: (state) => {
      state.passwordChangeError = null;
    },
    clearSettingsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Change Password
    builder
      .addCase(changePassword.pending, (state) => {
        state.passwordChangeLoading = true;
        state.passwordChangeError = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.passwordChangeLoading = false;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.passwordChangeLoading = false;
        state.passwordChangeError =
          typeof action.payload?.detail === 'string'
            ? action.payload.detail
            : 'Failed to change password';
      });

    // Deactivate Account
    builder
      .addCase(deactivateAccount.pending, (state) => {
        state.deactivateLoading = true;
      })
      .addCase(deactivateAccount.fulfilled, (state) => {
        state.deactivateLoading = false;
      })
      .addCase(deactivateAccount.rejected, (state) => {
        state.deactivateLoading = false;
      });

    // Investor Profile
    builder
      .addCase(getInvestorProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInvestorProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.investorProfile = action.payload;
      })
      .addCase(getInvestorProfile.rejected, (state, action) => {
        state.loading = false;
        state.error =
          typeof action.payload?.detail === 'string'
            ? action.payload.detail
            : 'Failed to fetch investor profile';
      });

    builder
      .addCase(updateInvestorProfile.pending, (state) => {
        state.error = null;
      })
      .addCase(updateInvestorProfile.fulfilled, (state, action) => {
        state.investorProfile = action.payload;
      })
      .addCase(updateInvestorProfile.rejected, (state, action) => {
        state.error =
          typeof action.payload?.detail === 'string'
            ? action.payload.detail
            : 'Failed to update investor profile';
      });

    // Subscription
    builder
      .addCase(getSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.subscription = action.payload;
      })
      .addCase(getSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error =
          typeof action.payload?.detail === 'string'
            ? action.payload.detail
            : 'Failed to fetch subscription';
      });

    // Transactions
    builder
      .addCase(getTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.transactions;
        state.transactionsTotal = action.payload.total;
      })
      .addCase(getTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error =
          typeof action.payload?.detail === 'string'
            ? action.payload.detail
            : 'Failed to fetch transactions';
      });
  },
});

export const { clearPasswordChangeError, clearSettingsError } =
  settingsSlice.actions;
export default settingsSlice.reducer;
